/**
 * Máscaras de formato BR para campos de formulário do funil comercial.
 *
 * Ficam no módulo `features/leads/` porque o uso nasceu aqui (cadastro de
 * lead + formulário público do onboarding, PR-M1-01b), mas são puros e
 * reutilizáveis em qualquer outro módulo do KmGestao — se virar
 * necessário fora do funil, migrar para `apps/web/src/lib/formatters.ts`
 * ou para um `@kmgestao/utils-br`.
 *
 * Todas as funções são **seguras com entrada nula/parcial** (retornam
 * string vazia ou o input original parcialmente formatado) para que o
 * usuário possa digitar progressivamente sem receber erros.
 */

/**
 * Remove tudo que não for dígito. Use antes de persistir no banco
 * (colunas de telefone/CPF/CNPJ guardam só números).
 */
export function normalizePhoneBR(input: string | null | undefined): string {
  if (!input) return '';
  return input.replace(/\D/g, '');
}

/**
 * Normaliza CEP removendo caracteres não-dígito. Use antes de persistir.
 */
export function normalizeCep(input: string | null | undefined): string {
  if (!input) return '';
  return input.replace(/\D/g, '').slice(0, 8);
}

/**
 * Aplica máscara brasileira padrão de CEP — `XXXXX-XXX`.
 * Funciona com input parcial (1-8 dígitos) e ignora máscara pré-existente.
 * Útil no autofill BrasilAPI, que retorna CEP como `35502048`.
 */
export function formatCep(input: string | null | undefined): string {
  const digits = normalizeCep(input);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

/**
 * Converte string em **Title Case brasileiro**: capitaliza a primeira
 * letra de cada palavra, exceto artigos/preposições curtas em pt-BR e
 * uma allowlist de acrônimos que devem manter CAIXA ALTA.
 *
 * Motivação: a Receita Federal (e por extensão o BrasilAPI) retorna
 * razão social + endereço em TUDO MAIÚSCULO — padrão dos cadastros
 * legados dos anos 90. Mostrar isso cru no form deixa visual
 * amador e polui o contrato futuro. Title Case respeita o dado
 * oficial sem parecer grito.
 *
 * **Allowlist de acrônimos** — termos que aparecem frequentemente em
 * razão social brasileira e precisam ficar em CAIXA ALTA mesmo dentro
 * do Title Case. Lista curta e específica; expandir conforme necessário.
 *
 * **Preposições minúsculas** — não capitaliza palavras de ligação
 * comuns em nomes pt-BR (`da`, `de`, `do`, etc.) exceto quando são
 * primeira palavra.
 *
 * @example
 *   toTitleCase('ALEXANDRE V. CALMON LTDA')       // 'Alexandre V. Calmon Ltda'
 *   toTitleCase('JOAO DA SILVA E CIA ME')         // 'Joao da Silva e Cia ME'
 *   toTitleCase('DIVINOPOLIS')                    // 'Divinopolis'
 *   toTitleCase('Dr Maria Silva EIRELI')          // 'Dr Maria Silva EIRELI'
 */
const TITLE_CASE_LOWER_WORDS = new Set([
  'a',
  'as',
  'da',
  'das',
  'de',
  'do',
  'dos',
  'e',
  'em',
  'na',
  'nas',
  'no',
  'nos',
  'o',
  'os',
  'para',
  'por',
]);

const TITLE_CASE_UPPER_WORDS = new Set([
  'ltda',
  'me',
  'epp',
  'eireli',
  'sa',
  's.a',
  's/a',
  'cia',
  'cnpj',
  'cpf',
]);

export function toTitleCase(input: string | null | undefined): string {
  if (!input) return '';

  // Regra do sistema: SEMPRE normaliza, inclusive inputs mixed case
  // digitados manualmente. Decisão de produto — queremos um único
  // padrão visual em toda a plataforma, não um "respeita o que o user
  // escreveu". Se alguém precisar de caixa mista específica (ex: marca
  // com grafia não-padrão), crie helper/opção dedicada.

  const words = input.trim().toLowerCase().split(/(\s+)/); // mantém os separadores para reconstruir

  return words
    .map((word, idx) => {
      if (word.trim() === '') return word; // separador puro

      const clean = word.replace(/[.,/]/g, '');

      if (TITLE_CASE_UPPER_WORDS.has(clean)) {
        return word.toUpperCase();
      }

      // Palavras de ligação: minúsculas, EXCETO se forem a primeira palavra
      // não-vazia da string (posição 0 ou 0 depois de whitespace inicial).
      const isFirst = words.slice(0, idx).every((w) => w.trim() === '');
      if (!isFirst && TITLE_CASE_LOWER_WORDS.has(clean)) {
        return word;
      }

      // Capitaliza primeira letra alfabética do token — tolerante a
      // strings que começam com pontuação (ex: `"(empresa)"`).
      return word.replace(/\p{L}/u, (c) => c.toUpperCase());
    })
    .join('');
}

/**
 * Aplica máscara progressiva ao CNPJ conforme o usuário digita.
 * Diferente de `formatCnpj` (que só formata com 14 dígitos exatos),
 * esta função trata qualquer prefixo parcial:
 *
 *   0 dígitos  → ""
 *   1–2        → "XX"
 *   3–5        → "XX.XXX"
 *   6–8        → "XX.XXX.XXX"
 *   9–12       → "XX.XXX.XXX/XXXX"
 *   13–14      → "XX.XXX.XXX/XXXX-XX"
 *
 * Usada em `onChange` de inputs de CNPJ para feedback visual imediato,
 * sem esperar o blur. Trunca em 14 dígitos.
 */
export function maskCnpjProgressive(input: string | null | undefined): string {
  if (!input) return '';
  const digits = input.replace(/\D/g, '').slice(0, 14);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  }
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

/**
 * Aplica máscara visual BR ao telefone conforme o usuário digita.
 *
 * Formatos suportados (progressivamente conforme a quantidade de dígitos):
 *
 *   0 dígitos  → ""
 *   1–2        → "(XX"
 *   3–6        → "(XX) XXXX"
 *   7–10       → "(XX) XXXX-XXXX"   (fixo)
 *   11         → "(XX) XXXXX-XXXX"  (celular com nono)
 *   > 11       → trunca pra 11 (limite BR). Se quiser internacional,
 *               expandir aqui ou criar `formatPhoneIntl`.
 *
 * Apropriado para uso inline em `onChange` — garante feedback visual
 * imediato. Sem dependência externa (não usa `imask`, `react-input-mask`
 * etc) — evita bloat e SSR edge cases.
 */
export function formatPhoneBR(input: string | null | undefined): string {
  const digits = normalizePhoneBR(input).slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;

  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);

  if (rest.length <= 4) {
    return `(${ddd}) ${rest}`;
  }
  if (rest.length <= 8) {
    // Fixo: (XX) XXXX-XXXX
    return `(${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
  }
  // Celular com 9 dígitos: (XX) XXXXX-XXXX
  return `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
}

/**
 * Formata uma data ISO em texto **relativo curto pt-BR** ("agora",
 * "há 7 min", "há 3h", "há 5 dias", "DD/MM/YYYY"). Quando passa de
 * 30 dias volta pra data absoluta, evitando "há 487 dias" inútil.
 *
 * Sem dependência de date-fns (~10KB) e sem passar pelo i18n —
 * consistente com `formatPhoneBR`, `formatCnpj`, `formatCep` deste
 * mesmo módulo: formatadores BR-locale-específicos retornam strings
 * já em pt-BR. Se a plataforma virar multi-locale, abstrair junto
 * com os demais formatters.
 *
 * Usado em cards e timelines onde "há X" comunica freshness sem gastar
 * largura ("Criado em 25 de abr. de 2026" → "Há 7 dias"). Pra labels
 * absolutas (cabeçalho de detalhe) use `toLocaleDateString` direto.
 *
 * @example
 *   formatRelativeDate('2026-04-24T10:00:00Z')  // "há 1 dia"
 *   formatRelativeDate(null)                     // "—"
 */
export function formatRelativeDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  let date: Date;
  try {
    date = new Date(iso);
  } catch {
    return '—';
  }
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return 'agora'; // datas no futuro tratamos como "agora"
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'agora';
  const min = Math.floor(sec / 60);
  if (min < 60) return `há ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `há ${hr}h`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'há 1 dia';
  if (day < 30) return `há ${day} dias`;
  return date.toLocaleDateString('pt-BR');
}

// ----------------------------------------------------------------------------
// Normalização do input do lead (regra do sistema)
// ----------------------------------------------------------------------------

import type { CreateLeadInput } from './schemas.js';

/**
 * Aplica a padronização global de texto do KmGestao em todos os campos
 * textuais do lead antes de persistir. **Esta é uma regra do sistema**
 * (decisão de produto 2026-04): queremos **um único padrão visual** em
 * toda a plataforma, independente de como o user digitou.
 *
 * Regras aplicadas:
 *
 *   - **Nome do responsável**: Title Case ("dr. joão" → "Dr. João")
 *   - **Razão social**: Title Case com acrônimos preservados
 *     ("ALEXANDRE V. CALMON LTDA" → "Alexandre V. Calmon Ltda")
 *   - **Nome fantasia**: Title Case
 *   - **Email**: lowercase + trim (já feito no schema Zod, reforçado aqui)
 *   - **Telefone**: formatado em (XX) XXXXX-XXXX (schema já aceita formato,
 *     reforçamos o formato canônico)
 *   - **Endereço → street/district/city/complement**: Title Case
 *   - **Endereço → uf**: UPPERCASE
 *   - **Endereço → cep**: formatado XXXXX-XXX
 *   - **Notas internas**: preservadas (texto livre do user)
 *
 * Use antes de passar ao hook de create/update do lead. Também pode
 * ser chamada no `onBlur` de inputs individuais para feedback visual
 * imediato (cada input aplica só o relevante).
 *
 * **Não é um tipo novo** — retorna `CreateLeadInput` com os mesmos
 * campos, apenas valores normalizados.
 */
export function normalizeLeadInput(input: CreateLeadInput): CreateLeadInput {
  return {
    ...input,
    full_name: toTitleCase(input.full_name),
    legal_name: input.legal_name ? toTitleCase(input.legal_name) : null,
    clinic_name_guess: input.clinic_name_guess ? toTitleCase(input.clinic_name_guess) : null,
    email: input.email.trim().toLowerCase(),
    phone: input.phone ? formatPhoneBR(input.phone) : null,
    address: {
      cep: input.address.cep ? formatCep(input.address.cep) : null,
      street: input.address.street ? toTitleCase(input.address.street) : null,
      number: input.address.number?.trim() ?? null,
      complement: input.address.complement ? toTitleCase(input.address.complement) : null,
      district: input.address.district ? toTitleCase(input.address.district) : null,
      city: input.address.city ? toTitleCase(input.address.city) : null,
      uf: input.address.uf ? input.address.uf.trim().toUpperCase().slice(0, 2) : null,
    },
  };
}
